import Vue from 'vue';
import { api } from 'src/plugins/axios'

/**
 * v-places-autocomplete: Google address suggestions under an address input.
 *
 *   v-places-autocomplete="placeChange"
 *   v-places-autocomplete="{ onSelect: placeChange, classCode }"   (no login)
 *
 * Calls back with `{ address_components: [{ long_name, short_name, types }] }`
 * for the picked address, the shape the legacy Maps widget produced.
 *
 * Suggestions come from Places API (New) through the API (/address/places),
 * which holds the Google key. The public registration page has no login, so
 * it passes its class code and uses the /address/public/places endpoints.
 */

const MIN_INPUT_LENGTH = 3
const DEBOUNCE_MS = 250
const BLUR_CLOSE_MS = 150

// Google bills suggestion requests that share a token, and end in a details
// request for the picked place, as one session.
function newSessionToken() {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.prototype.map.call(bytes, b => ('0' + b.toString(16)).slice(-2)).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

function options(binding) {
  const value = binding && binding.value
  if (typeof value === 'function') return { onSelect: value, classCode: null }
  return {
    onSelect: value && value.onSelect,
    classCode: value && value.classCode,
  }
}

function endpoint(state, action) {
  return state.options.classCode
    ? `/address/public/places/${action}`
    : `/address/places/${action}`
}

async function post(state, action, body) {
  if (state.options.classCode) body.classCode = state.options.classCode

  // The shared axios instance resolves undefined on a network error.
  const res = await api.post(endpoint(state, action), body)
  const data = res && res.data
  return data && data.success ? data.payload : null
}

function createMenu() {
  const menu = document.createElement('div')
  menu.className = 'places-autocomplete-menu'
  menu.style.display = 'none'
  document.body.appendChild(menu)
  return menu
}

function position(state) {
  const rect = state.input.getBoundingClientRect()
  const menu = state.menu
  menu.style.top = `${rect.bottom + 2}px`
  menu.style.left = `${rect.left}px`
  menu.style.minWidth = `${Math.max(rect.width, 280)}px`
}

function close(state) {
  state.open = false
  state.highlighted = -1
  state.menu.style.display = 'none'
  window.removeEventListener('scroll', state.onReposition, true)
  window.removeEventListener('resize', state.onReposition)
}

function render(state) {
  const menu = state.menu
  while (menu.firstChild) menu.removeChild(menu.firstChild)

  if (!state.suggestions.length || document.activeElement !== state.input) {
    close(state)
    return
  }

  state.suggestions.forEach((suggestion, index) => {
    const item = document.createElement('div')
    item.className = 'places-autocomplete-item' + (index === state.highlighted ? ' highlighted' : '')

    const main = document.createElement('span')
    main.className = 'main'
    main.textContent = suggestion.mainText || suggestion.text
    item.appendChild(main)

    if (suggestion.secondaryText) {
      const secondary = document.createElement('span')
      secondary.className = 'secondary'
      secondary.textContent = suggestion.secondaryText
      item.appendChild(secondary)
    }

    // mousedown, not click: keeps focus in the input, so the field's own
    // blur handling does not run before the address is filled in.
    item.addEventListener('mousedown', event => {
      event.preventDefault()
      select(state, index)
    })
    item.addEventListener('mouseenter', () => {
      state.highlighted = index
      Array.prototype.forEach.call(menu.children, (child, i) => {
        if (child.classList) child.classList.toggle('highlighted', i === index)
      })
    })

    menu.appendChild(item)
  })

  // Required by Google's terms when suggestions are shown without a map.
  const attribution = document.createElement('div')
  attribution.className = 'places-autocomplete-attribution'
  attribution.textContent = 'Powered by Google'
  menu.appendChild(attribution)

  position(state)
  menu.style.display = 'block'

  if (!state.open) {
    state.open = true
    window.addEventListener('scroll', state.onReposition, true)
    window.addEventListener('resize', state.onReposition)
  }
}

async function search(state) {
  const input = state.input.value.trim()
  const request = ++state.requestId

  if (input.length < MIN_INPUT_LENGTH) {
    state.suggestions = []
    render(state)
    return
  }

  if (!state.sessionToken) state.sessionToken = newSessionToken()

  let payload = null
  try {
    payload = await post(state, 'autocomplete', { input, sessionToken: state.sessionToken })
  } catch (err) {
    payload = null
  }

  // A slower answer for something typed earlier.
  if (request !== state.requestId) return

  state.suggestions = (payload && payload.suggestions) || []
  state.highlighted = -1
  render(state)
}

async function select(state, index) {
  const suggestion = state.suggestions[index]
  if (!suggestion) return

  const sessionToken = state.sessionToken
  state.sessionToken = null
  state.requestId++
  state.suggestions = []
  close(state)

  let payload = null
  try {
    payload = await post(state, 'details', { placeId: suggestion.placeId, sessionToken })
  } catch (err) {
    payload = null
  }

  const callback = state.options.onSelect
  if (payload && payload.place && typeof callback === 'function') {
    callback(payload.place)
  }
}

function onKeydown(state, event) {
  if (!state.open) {
    if (event.key === 'ArrowDown' && state.suggestions.length) {
      render(state)
      event.preventDefault()
    }
    return
  }

  const count = state.suggestions.length

  switch (event.key) {
    case 'ArrowDown':
      state.highlighted = (state.highlighted + 1) % count
      render(state)
      event.preventDefault()
      break
    case 'ArrowUp':
      state.highlighted = state.highlighted <= 0 ? count - 1 : state.highlighted - 1
      render(state)
      event.preventDefault()
      break
    case 'Enter':
      if (state.highlighted >= 0) {
        event.preventDefault()
        event.stopPropagation()
        select(state, state.highlighted)
      }
      break
    case 'Escape':
      // Close the list without also closing the modal the field is in,
      // which Quasar does on the keyup.
      event.stopPropagation()
      state.swallowEscapeKeyup = true
      close(state)
      break
    case 'Tab':
      close(state)
      break
  }
}

const PlacesAutocomplete = {
  inserted(el, binding) {
    const input = el.tagName === 'INPUT' ? el : el.querySelector('input')
    if (!input) return

    input.placeholder = ''
    // Keep the browser's own autofill list from covering the suggestions.
    input.setAttribute('autocomplete', 'new-password')

    const state = {
      input,
      options: options(binding),
      menu: createMenu(),
      suggestions: [],
      highlighted: -1,
      open: false,
      requestId: 0,
      sessionToken: null,
      timer: null,
      swallowEscapeKeyup: false,
    }

    state.onReposition = () => position(state)
    state.onInput = () => {
      clearTimeout(state.timer)
      state.timer = setTimeout(() => search(state), DEBOUNCE_MS)
    }
    state.onKeydown = event => onKeydown(state, event)
    state.onKeyup = event => {
      if (event.key === 'Escape' && state.swallowEscapeKeyup) {
        state.swallowEscapeKeyup = false
        event.stopPropagation()
      }
    }
    state.onBlur = () => {
      clearTimeout(state.timer)
      setTimeout(() => close(state), BLUR_CLOSE_MS)
    }
    state.onMouseenter = () => input.setAttribute('autocomplete', 'new-password')

    input.addEventListener('input', state.onInput)
    input.addEventListener('keydown', state.onKeydown)
    input.addEventListener('keyup', state.onKeyup)
    input.addEventListener('blur', state.onBlur)
    el.addEventListener('mouseenter', state.onMouseenter)

    el.__placesAutocomplete = state
  },

  componentUpdated(el, binding) {
    const state = el.__placesAutocomplete
    if (state) state.options = options(binding)
  },

  unbind(el) {
    const state = el.__placesAutocomplete
    if (!state) return

    clearTimeout(state.timer)
    close(state)
    state.input.removeEventListener('input', state.onInput)
    state.input.removeEventListener('keydown', state.onKeydown)
    state.input.removeEventListener('keyup', state.onKeyup)
    state.input.removeEventListener('blur', state.onBlur)
    el.removeEventListener('mouseenter', state.onMouseenter)
    if (state.menu.parentNode) state.menu.parentNode.removeChild(state.menu)
    delete el.__placesAutocomplete
  }
}

Vue.directive('places-autocomplete', PlacesAutocomplete)


export default PlacesAutocomplete
